import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components/macro';
import dayjs from 'dayjs';
import { useHistory } from 'react-router';
import { theme } from 'lib/style';
import Select from 'react-select';
import { maxBy } from 'lodash';
import { calculateGraphData, calculateRange } from 'lib/utils/functions';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ComposedChart,
  ResponsiveContainer,
  Area,
} from 'recharts';
import { getSmsOptions, getSmsOptionsMapping } from 'lib/const';
import { TooltipChart } from './TooltipChart';
import { LoadingIndicator, MainContainer } from 'lib/components';
import { useQuerySmsOverview } from 'lib/api/sms/useQuerySmsOverview';
import {
  SMSGraphData,
  SmsOverviewParams,
  UnansweredChats,
} from 'lib/api/sms/types';
import { Trans, useTranslation } from 'react-i18next';
import { ModalDateRangeSelector } from 'app/pages/reports/components/DateRangeSelector/ModalDateRangeSelector';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';
import { EXTEND_DELETION_TYPES } from 'lib/const/ModalVideoDeletionApproval';
import { SMS_FILTER_KEY } from 'lib/const/SmsOptions';

dayjs.extend(weekOfYear);
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);

const FiltersContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${theme.mediaQueryMinWidth.sm} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const Filter = styled.div`
  float: right;
  display: flex;
  justify-content: space-between;
  ${theme.mediaQueryMinWidth.sm} {
    width: 500px;
  }
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  ${theme.mediaQueryMaxWidth.md} {
    flex-direction: column;
  }
`;

const ContentLeft = styled.div`
  width: 49%;
  ${theme.mediaQueryMaxWidth.md} {
    width: 100%;
  }
`;

const ContentRight = styled.div`
  width: 49%;
  ${theme.mediaQueryMaxWidth.md} {
    width: 100%;
  }
`;

const GraphContainer = styled.div`
  width: 100%;
  border: 1px solid #f7f8fa;
  box-shadow: 0px 4px 12px rgba(29, 30, 36, 0.04);
  height: auto;
  min-height: 400px;
  position: relative;
  .graph--header {
    padding: 20px;
    max-height: 100px;
    .wrapper {
      display: flex;
      .wrapper--div {
        padding: 20px 20px 5px 20px;
      }
      h3 {
        color: ${theme.palette.primaryDarkBlue};
      }
    }
  }
  .graph--body {
    width: 100%;
    height: 400px;
  }
  .filter {
    position: absolute;
    right: 30px;
  }
`;

const BarGraphContainer = styled.div`
  width: 100%;
  border: 1px solid #f7f8fa;
  box-shadow: 0px 4px 12px rgba(29, 30, 36, 0.04);
  height: auto;
  min-height: 400px;
  margin: 50px 0 0 0;
  .graph--body {
    height: 400px;
    padding: 20px;
  }
  .bargraph--header {
    padding: 20px;
    display: flex;
    justify-content: space-between;
  }
`;

const RowHeadRight = styled.div`
  display: flex;
  justify-content: space-between;
  div {
    border: 1px solid #f7f8fa;
    box-shadow: 0px 4px 12px rgba(29, 30, 36, 0.04);
    padding: 20px;
    line-height: 1.5;
    width: 40%;
  }
`;

const MessageContainer = styled.div`
  width: 100%;
  border: 1px solid #f7f8fa;
  box-shadow: 0px 4px 12px rgba(29, 30, 36, 0.04);
  margin-top: 30px;
  .message--header {
    display: flex;
    width: 100%;
    justify-content: space-between;
    h3 {
      color: ${theme.palette.primaryRedDanger};
    }
    span {
      color: ${theme.palette.primaryDarkBlue};
    }
    label,
    h3 {
      padding: 0 20px 0 20px;
    }
    .sub--header {
      color: #9297a2;
    }
    .see--more {
      text-decoration: underline;
      cursor: pointer;
    }
  }
`;

const MessageBody = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 20px 0 20px 0;
  .message--header {
    padding-top: 10px;
  }
  .danger {
    color: ${theme.palette.buttonDanger};
  }
  &.no--record {
    justify-content: center;
  }
`;

const Badge = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  background: ${props => props.color};
  border-radius: 5px;
`;

const Flex = styled.div`
  display: flex;
  align-items: center;
`;

const NoData = styled.div`
  display: flex;
  justify-content: center;
`;

const DateLabel = styled.span`
  color: #9297a2;
`;

export const SmsOverviewLayout = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const smsOptions = getSmsOptions(t);
  const getInitialFilter = () => {
    const saved = localStorage.getItem(SMS_FILTER_KEY);

    if (!saved) {
      return { interval: smsOptions[1].value };
    }

    try {
      const parsed = JSON.parse(saved);
      if (parsed.range === EXTEND_DELETION_TYPES.CUSTOM) {
        return {
          interval: EXTEND_DELETION_TYPES.CUSTOM,
          startDate: new Date(parsed.startDate),
          endDate: new Date(parsed.endDate),
        };
      }
      return { interval: parsed };
    } catch {
      return { interval: saved };
    }
  };

  const calculateCustomRange = (
    startDate: Date,
    endDate: Date,
    format?: { formatFrom: string; formatTo: string }
  ) => {
    const from = dayjs(startDate).startOf('day').utc().format();

    const to = dayjs(endDate).endOf('day').utc().format();

    return {
      from,
      to,
      formatFrom: format ? dayjs(from).format(format.formatFrom) : '',
      formatTo: format ? dayjs(to).format(format.formatTo) : '',
    };
  };

  const initial = getInitialFilter();

  const [intervalRange, setIntervalRange] = useState(initial.interval);
  const [customStartDate, setCustomStartDate] = useState<Date | null>(
    initial.startDate || null
  );
  const [customEndDate, setCustomEndDate] = useState<Date | null>(
    initial.endDate || null
  );

  const [dateRange, setDateRange] = useState<SmsOverviewParams>(() => {
    if (
      initial.interval === EXTEND_DELETION_TYPES.CUSTOM &&
      initial.startDate &&
      initial.endDate
    ) {
      return calculateCustomRange(initial.startDate, initial.endDate);
    }
    return calculateRange(initial.interval);
  });

  const [graphData, setGraphData] = React.useState<any>([]);
  const [graphDomain, setGraphDomain] = React.useState(0);
  const [barDomain, setBarDomain] = React.useState(0);
  const [graphCount, setGraphCount] = React.useState<any>({
    sentCount: 0,
    receivedCount: 0,
  });
  const [showRangeModal, setShowRangeModal] = useState(false);
  const themes = useTheme();

  const getOverviewStats = async () => {
    await loadSmsOverview();
  };

  const getBucketType = (
    from: string | Date,
    to: string | Date,
    interval: string
  ): 'DAY' | 'WEEK' | 'MONTH' | 'YEAR' => {
    if (interval === EXTEND_DELETION_TYPES.CUSTOM) {
      const diffDays = dayjs(to).diff(dayjs(from), 'day');

      if (diffDays <= 31) return 'DAY';
      if (diffDays <= 120) return 'WEEK';
      if (diffDays <= 730) return 'MONTH';
      return 'YEAR';
    }

    if (interval === 'YTD') return 'MONTH';
    if (interval === 'MTD') return 'DAY';
    if (interval === '7D') return 'DAY';
    if (interval === '30D') return 'DAY';
    if (interval === '90D') return 'WEEK';

    return 'DAY';
  };

  const {
    data: overview,
    isLoading: loading,
    refetch: loadSmsOverview,
  } = useQuerySmsOverview({
    from: dateRange.from,
    to: dateRange.to,
  });

  const calculateDifferenceInTime = (value: any) => {
    const dateCurrent = dayjs();
    const day: any = dateCurrent.diff(value, 'day', true);
    const hour: any = dateCurrent.diff(value, 'hour', true);
    if (parseInt(day, 10) > 0) {
      return { value: `${parseInt(day, 10)} day(s)`, type: 'day' };
    }
    return { value: `${parseInt(hour, 10)} hour(s)`, type: 'hour' };
  };

  const getGraphStructure = (): {
    structure: SMSGraphData[];
    bucket: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR';
  } => {
    if (!dateRange?.from || !dateRange?.to) {
      return {
        structure: [],
        bucket: 'DAY',
      };
    }

    const bucket = getBucketType(dateRange.from, dateRange.to, intervalRange);

    const dateArray: SMSGraphData[] = [];

    if (intervalRange === 'YTD') {
      const startOfYear = dayjs().startOf('year');

      for (let i = 0; i < 12; i++) {
        const month = startOfYear.add(i, 'month');
        dateArray.push({
          dateDisplay: month.format('MMM'),
          sentCount: '0',
          receivedCount: '0',
        });
      }
    } else {
      const start = dayjs(dateRange.from);
      const end = dayjs(dateRange.to);

      let cursor = start.startOf(
        bucket === 'DAY'
          ? 'day'
          : bucket === 'WEEK'
            ? 'week'
            : bucket === 'MONTH'
              ? 'month'
              : 'year'
      );

      while (cursor.isBefore(end) || cursor.isSame(end)) {
        dateArray.push({
          dateDisplay:
            bucket === 'DAY'
              ? cursor.format('MM/DD')
              : bucket === 'WEEK'
                ? `${cursor.year()}-${cursor.week()}`
                : bucket === 'MONTH'
                  ? cursor.format('MMM YYYY')
                  : cursor.format('YYYY'),
          sentCount: '0',
          receivedCount: '0',
        });

        cursor = cursor.add(
          1,
          bucket === 'DAY'
            ? 'day'
            : bucket === 'WEEK'
              ? 'week'
              : bucket === 'MONTH'
                ? 'month'
                : 'year'
        );
      }
    }

    return { structure: dateArray, bucket };
  };

  const onCustomRangeSelected = (
    startDate: Date | null,
    endDate: Date | null
  ) => {
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
    setIntervalRange(EXTEND_DELETION_TYPES.CUSTOM);

    setDateRange(calculateCustomRange(startDate!, endDate!));

    localStorage.setItem(
      SMS_FILTER_KEY,
      JSON.stringify({
        range: EXTEND_DELETION_TYPES.CUSTOM,
        startDate,
        endDate,
      })
    );

    setShowRangeModal(false);
  };

  useEffect(() => {
    if (!overview?.graph) return;

    const { structure, bucket } = getGraphStructure();

    const {
      sent,
      received,
      maxDomain,
      graphData: mergedGraphData,
    } = calculateGraphData(overview.graph as SMSGraphData[], structure, bucket);

    setGraphDomain(maxDomain);
    setGraphCount({ sentCount: sent, receivedCount: received });
    setGraphData(mergedGraphData);

    if (overview?.mostActiveUsers?.length) {
      setBarDomain(
        parseInt(
          maxBy(overview?.mostActiveUsers, u => parseInt(u?.sentCount || '0'))
            ?.sentCount || '0'
        )
      );
    }
  }, [overview?.graph, dateRange]);

  useEffect(() => {
    if (
      intervalRange === EXTEND_DELETION_TYPES.CUSTOM &&
      customStartDate &&
      customEndDate
    ) {
      setDateRange(calculateCustomRange(customStartDate, customEndDate));
    } else {
      setDateRange(calculateRange(intervalRange));
    }
  }, [intervalRange, customStartDate, customEndDate]);

  useEffect(() => {
    if (
      intervalRange === EXTEND_DELETION_TYPES.CUSTOM &&
      customStartDate &&
      customEndDate
    ) {
      setDateRange(calculateCustomRange(customStartDate!, customEndDate!));
    } else {
      setDateRange(calculateRange(intervalRange));
    }
  }, [intervalRange, customStartDate, customEndDate]);

  useEffect(() => {
    getOverviewStats();
  }, [dateRange]);

  if (loading) {
    return <LoadingIndicator isLoading={loading} height='750px' />;
  }

  return (
    <>
      <MainContainer>
        <FiltersContainer>
          <Filter>
            <h3>{t('Overview')}</h3>
          </Filter>
        </FiltersContainer>
        <Content>
          <ContentLeft>
            <GraphContainer>
              <div className='graph--header'>
                <div className='wrapper'>
                  <div className='wrapper--div'>
                    <Flex>
                      <Badge color={themes.colors.primary[100]} />
                      &nbsp; {t('Received')}
                    </Flex>{' '}
                    <h3>{graphCount.receivedCount}</h3>
                  </div>
                  <div className='wrapper--div'>
                    <Flex>
                      <Badge color='#B6E0EC' />
                      &nbsp; {t('Sent')}
                    </Flex>{' '}
                    <h3>{graphCount.sentCount}</h3>
                  </div>
                  <div className='filter'>
                    <Select
                      styles={{
                        control: (base: any) => ({
                          ...base,
                          height: '40px',
                          width: '150px',
                        }),
                        indicatorSeparator: () => ({ display: 'none' }),
                      }}
                      options={smsOptions}
                      menuPortalTarget={document.body}
                      menuPlacement={'bottom'}
                      isSearchable={false}
                      value={smsOptions.find(o => {
                        return o.value === intervalRange;
                      })}
                      onChange={(option: any) => {
                        if (option.value === EXTEND_DELETION_TYPES.CUSTOM) {
                          setShowRangeModal(true);
                        } else {
                          setIntervalRange(option.value);
                          localStorage.setItem(
                            SMS_FILTER_KEY,
                            JSON.stringify(option.value)
                          );
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
              <br />
              <div className='graph--body'>
                <ResponsiveContainer>
                  <ComposedChart
                    width={500}
                    height={300}
                    data={graphData}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='dateDisplay' />
                    <YAxis allowDecimals={false} domain={[0, graphDomain]} />
                    <Tooltip
                      content={
                        <TooltipChart
                          headerKey='dateDisplay'
                          isAreaChart={true}
                        />
                      }
                    />
                    {/* <Legend /> */}
                    <Area
                      type='linear'
                      dataKey='receivedCount'
                      stroke={themes.colors.primary[100]}
                      strokeWidth={2}
                      fillOpacity={0}
                    />
                    <Area
                      type='linear'
                      dataKey='sentCount'
                      stroke='#B6E0EC'
                      strokeWidth={2}
                      fillOpacity={0}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </GraphContainer>
            <BarGraphContainer>
              <div className='bargraph--header'>
                <div>{t('Most Active Users (by sent)')}</div>
                <DateLabel>
                  {
                    getSmsOptionsMapping(t)[
                      intervalRange as keyof ReturnType<
                        typeof getSmsOptionsMapping
                      >
                    ]
                  }
                </DateLabel>
              </div>
              <br />
              <div className='graph--body'>
                <ResponsiveContainer>
                  {overview?.mostActiveUsers?.length ? (
                    <BarChart
                      width={150}
                      height={40}
                      data={overview?.mostActiveUsers}
                    >
                      <Bar
                        dataKey='sentCount'
                        fill='rgba(0, 27, 83, 0.8)'
                        radius={[10, 10, 0, 0]}
                      />
                      <XAxis dataKey='firstName' />
                      <YAxis allowDecimals={false} domain={[0, barDomain]} />
                      <Tooltip
                        cursor={{ fill: 'transparent' }}
                        content={
                          <TooltipChart
                            displayKey='sentCount'
                            headerKey='firstName'
                          />
                        }
                      />
                    </BarChart>
                  ) : (
                    <NoData>{t('No record found')}</NoData>
                  )}
                </ResponsiveContainer>
              </div>
            </BarGraphContainer>
          </ContentLeft>
          <ContentRight>
            <RowHeadRight>
              <Trans
                i18nKey='activeChatsWithCount'
                components={{
                  br: <br />,
                  strong: (
                    <strong
                      style={{ cursor: 'pointer' }}
                      onClick={() => history.push('/sms/messages')}
                    />
                  ),
                }}
                values={{ count: overview?.chatCount }}
              />
              {/* <div>
                Avg. Speed of Answer
                <br />
                <strong>3:21</strong> min
              </div> */}
            </RowHeadRight>
            <MessageContainer>
              <div className='message--header' style={{ paddingTop: '20px' }}>
                <label>{t('Unanswered messages')}</label>
                <label
                  className='see--more'
                  onClick={() => history.push('/sms/messages')}
                >
                  {t('See more')}
                </label>
              </div>
              <div className='message--header'>
                <h3>{overview?.unansweredChats?.[1]}</h3>
              </div>
              <div className='message--header'>
                <label className='sub--header'>{t('Customer')}</label>
                <label className='sub--header'>{t('No response for')}</label>
              </div>
              <MessageBody
                className={`${
                  !overview?.unansweredChats?.[0].length ? 'no--record' : ''
                }`}
              >
                {overview?.unansweredChats?.[0].length ? (
                  overview?.unansweredChats?.[0].map(
                    (e: UnansweredChats, i: number) => {
                      const diffObj = calculateDifferenceInTime(e.createdAt);
                      return (
                        <div className='message--header'>
                          <label>
                            <span className='sub--header'>{i + 1}</span> &nbsp;
                            <span>
                              {e.contactFirstName || e.contactLastName
                                ? `${e.contactFirstName} ${e.contactLastName}`
                                : e.contactPhone}
                            </span>
                          </label>
                          <label
                            className={diffObj.type === 'day' ? 'danger' : ''}
                          >
                            {diffObj.value}
                          </label>
                        </div>
                      );
                    }
                  )
                ) : (
                  <div>
                    {t('No record found')}
                    {`.`}
                  </div>
                )}
              </MessageBody>
            </MessageContainer>
          </ContentRight>
        </Content>
        {showRangeModal && (
          <ModalDateRangeSelector
            initStartDate={customStartDate || new Date()}
            initEndDate={customEndDate || new Date()}
            handleModalClose={() => setShowRangeModal(false)}
            onRangeSelected={onCustomRangeSelected}
          />
        )}
      </MainContainer>
    </>
  );
};
